import { CmcWebApiTypes } from '@cmctechnology/webinvest-api-client-web-trading';

export type ClientSettings = Partial<{
  MinimumOrderValue: number;
  MinimumOrderValueInternational: number;
  HelpdeskLabel: string;
  HelpdeskPhone: string;
  AuExchangeSelectionEnabled: boolean;
  IsBMREnabled: boolean;
  BestPriceMarketOrders: boolean;
  TheScreenerEnabled: boolean;
  TracrsEnabled: boolean;
  ShowUpdateDetailsReminder: boolean;
  CanTrade: boolean;
  CredentialManagementEnabled: boolean;
  WhitelabelClient: boolean;
  SessionTimeoutSettingEnabled: boolean;
  BuyCreditsEnabled: boolean;
  OptionsQuizEnabled: boolean;
  MarginOptionsVetting: boolean;
  IsPortfolioAnalyticsEnabled: boolean;
  USTaxFormsDownloadEnabled: boolean;
  NextGenSsoEnabled: boolean;
  UseOptionsMarginGet2Endpoint: boolean;
  TradingPinMandatory: boolean;
  'AlgoOrders.Enabled': boolean;
  'Access.AlgoOrders': boolean;
  'Access.Pro.FixedLayouts': boolean;
  'Access.Pro.LightTheme': boolean;
  USExtendedHours: boolean;
  FalconAccess: boolean;
  FalconEnabled: boolean;
  HasFreeTrades: boolean;
  'Pro.Enabled': boolean;
  ForgerockEnabled: boolean;
  AccessFalconToggle: boolean;
  MfaUIEnabled: boolean;
  FalconEnableToggleUpdatedDate: string;
}>;

export enum ClientRoles {
  CLIENT = 'Client',

  TRADES_CONTINGENT_ORDERS = 'ContingentOrders',
  TRADES_SHARES_AT_MARKET = 'MarketToLimitAllowed',
  MARGIN_LOANS = 'MarginLending.Provider',

  UPDATE_WAREHOUSE_HOLDINGS = 'Gbst.UpdateWHHoldings',
  UPDATE_CASH_BALANCE = 'Gbst.UpdateCashBalance',

  SHARESIGHT_ACTIVE = 'ShareSight.IsActive',
  SHARESIGHT_PRO = 'ShareSight.Pro',
  TRADINGCENTRAL_WEBDATA_ACCESS = 'TradingCentral.WebData',
  // Access.Praemium is the first version of tax tools. Once the newer version of Tax Tools is made accessible
  // to ANZ+SGB by granting 'Access.TaxTools' access, the 'Access.Praemium' access permission can be deleted
  PRAEMIUM_ACTIVE = 'Access.Praemium',
  TAXTOOLS_ACTIVE = 'Access.TaxTools',

  RECKON_ACTIVE = 'Access.Reckon',

  ACCESS_MOBILE = 'Access.Mobile',
  ACCESS_PRO = 'Access.Pro',
  ACCESS_PRO_TYPE = 'Access.Pro.Type',

  SALESFORCE_CHAT = 'Salesforce.Chat',

  ESG_ENABLED = 'ESG.Enabled',

  ID_VERIFICATION_REQUIRED = 'Id.Verification.Required',
  ID_REVERIFICATION_ACCOUNTTOOOLD = 'Id.ReVerification.AccountTooOld',
  ID_REVERIFICATION_MISSINGDOB = 'Id.ReVerification.MissingDob',

  // TRANSFER_FUNDS roles are used for transferFunds tab control
  TRANSFER_FUNDS_ALLOWED = 'TransferFunds.Allowed',
  TRANSFER_FUNDS_VIEWINTERESTRATE = 'TransferFunds.ViewInterestRate',
  TRANSFER_FUNDS_VIEWHOWTODEPOSIT = 'TransferFunds.ViewHowToDeposit',
  TRANSFER_FUNDS_VIEWHOWTOWITHDRAW = 'TransferFunds.ViewHowToWithdraw',
  TRANSFER_FUNDS_VIEWEBASETUP = 'TransferFunds.ViewExternalBankAccountSetup',
  TRANSFER_FUNDS_VIEWNOCASHACCOUNT = 'TransferFunds.ViewNoCashAccount',

  TRADINGINSIGHTS_ACCESS = 'TradingInsights.Access',

  TRADING_TRAILINGSTOP = 'Trading.TrailingStop',

  CFD_EDUCATION = 'Cfd.Education',

  SCREENER_ACCESS_NORMAL = 'Screener.Access.Normal',
  SCREENER_ACCESS_HIGHER = 'Screener.Access.Higher',

  ONMARKET_ACCESS_NORMAL = 'OnMarket.Access.Normal',
  INITIALPUBLICOFFERING_ACCESS = 'InitialPublicOffering.Access',

  MORNINGSTAR_QUANTITATIVE_ACCESS = 'Morningstar.Quantitative.Access',

  /// <summary>
  /// ASX Direct Market Access
  /// </summary>
  ACCESS_ASX_DMA = 'Access.ASX.DMA',

  // Access AMFS - ASX Managed Funds Service
  ACCESS_ASXMFUND = 'Access.ASXmFund',

  /// <summary>
  /// ASX Broker
  /// </summary>
  ACCESS_ASX_BROKER = 'Access.ASX.Broker',

  TRADE_OPTIONS_STANDARD = 'Trade.Options.Standard',

  TRADINGPIN_OPTIONAL = 'TradingPin.Optional',

  SITE_ADMINISTRATOR = 'Access.SiteAdministrator',

  PORTFOLIO_ANALYTICS_ENABLED = 'Access.PortfolioAnalytics.Enabled',

  INTERNATIONAL_USTAXFORMS_ENABLED = 'International.USTaxForms.Enabled',

  NEXT_GEN_SSO_ENABLED = 'Access.NextGenSso.Enabled',

  INTERMEDIARY_COMPANY_MEMBER = 'IntermediaryCompanyMember',
  INTERMEDIARY_REPORTING_ACCESS = 'IntermediaryReportingAccess',

  BULK_ORDERS_AVAILABLE = 'BulkOrdersAvailable',

  ALLOW_APPLY_APPLICATON_ONBEHALF = 'AllowOnlineApplication',
  ALLOW_BULK_APPLY = 'AllowBulkApplication',
  ADVISER_CAN_BULK_APPLY = 'Intermediary.CanBulkApply',
  ALLOW_ACCOUNT_ALLOCATION_EDIT = 'AllowAccountAllocationEdit',

  CLIENT_ONLINE_CHANGE_AVAILABLE = 'ClientOnlineChange.Allowed',

  // Theme access
  PRO_ENABLED = 'Pro.Enabled',
  PRO_SUBCRIPTION_ENABLED = 'Pro.Subscription.Enabled',

  ACCESS_ALGOS = 'Access.AlgoOrders',

  ACCESS_CRYPTO = 'Access.Crypto',

  // Access CHIX
  ACCESS_CHIX = 'Access.Chix',

  ACCESS_CHIX_TRACR = 'Access.Chix.TraCR',

  TRADE_OPTIONS = 'Trading.Options',

  TRADE_OPTIONS_MAX_LEVEL = 'Trading.OptionsMaxLevel', // This is the override value set by Helpdesk

  ACCESS_INTERNATIONALS = 'Internationals.Enabled',

  QANTAS_FREQUENT_FLYER_AVAILABLE = 'QantasFrequentFlyerAvailable',

  LOGGING_VERBOSITY_DETAILED = 'Logging.Verbosity.Detailed',

  ACCESS_OPTIONS_QUIZ = 'Trading.OptionsQuizEnabled',

  MARGIN_OPTIONS_VETTING = 'Margin.Options.Vetting',

  AEOI_ENABLED = 'Aeoi.Enabled',

  CONFIRMATION_ENABLED = 'Confirmation.Enabled',
  CONFIRMATION_V2_ENABLED = 'Confirmation.V2.Enabled',

  //Options margin endpoint
  USE_OPTIONS_MARGIN_GET2_ENDPOINT = 'Options.UseOptionsMarginGet2',

  //Options Margin Service
  USE_OPTIONS_MARGIN_SERVICE = 'Options.UseOptionsMarginService',

  //Options Greeks Service
  USE_OPTIONS_GREEKS_SERVICE = 'Options.UseOptionsGreeksService',

  ACCOUNT_CLOSURE_ENABLED = 'AccountClosure.Enabled',

  ACCESS_PRO_FIXED_LAYOUTS = 'Access.Pro.FixedLayouts',
  ACCESS_PRO_LIGHT_THEME = 'Access.Pro.LightTheme',
  ACCESS_RIVKIN_PORTFOLIOS = 'Access.RivkinPortfolios',

  ACCESS_US_EXTENDED_HOURS = 'Access.USExtendedHours',

  ACCESS_FALCON = 'Access.Falcon',
  FALCON_ENABLED = 'Falcon.Enabled',
  ACCESS_FALCONTOGGLE = 'Access.FalconToggle',
  ACCESS_FALCON_HEATMAPS = 'Access.Falcon.HeatMaps',
  ACCESS_TIPRANKS_ENABLED = 'Access.TipRanks.Enabled',
  FORGEROCK_ENABLED = 'Forgerock.Enabled',
  MODERN_INTERCEPTOR_ENABLED = 'ModernInterceptor.Enabled',
  ACCESS_MFA = 'MfaUI.Enabled',

  SECTOR_ANALYSIS_ENABLED = 'SectorAnalysis.Enabled',

  //Project Warren - Transition Anz To Cmc
  TRANSITION_ANZ_TO_CMC = 'TransitionAnzToCmcProjectWarren',

  TRADE_MIDPOINT_ENABLED = 'Trading.MidPointOrder.Enabled',

  EXCEL_ADD_IN_ENABLED = 'ExcelAddIn.Enabled',

  ACCESS_MODULE_WFE_CHART = 'Access.Module.WfeChart',
  // Raffles - Singapore
  STOCK_FILTER_ENABLED = 'StockFilter.Enabled',
  NOTIFICATION_DELETION_ENABLED = 'Notification.Deletion.Enabled',
  NEWS_AUSTRALIAN_ENABLED = 'News.Australian.Enabled',
  ACCESS_ALERTS = 'Access.Alerts',
  TRANSFER_SECURITIES_ENABLED = 'TransferSecurities.Enabled',
  ACCESS_AU_DIRECT_OWNERSHIP = 'Access.Au.DirectOwnership',

  STATEMENT_EOFY = 'Statements.EndOfYear',
  STATEMENT_MONTHLY = 'Statements.Monthly',

  PRICING_SUBSCRIPTION_ENABLED = 'PricingSubscription.Enabled',

  ACCESS_MODULE_WFE_ESG = 'Access.Module.WfeEsg',

  // Singapre
  ACCESS_XSEX_SGD_ONLY = 'Access.XSEX.SGDOnly',

  ACCESS_MARKETDATA_SUBSCRIBER = 'Access.MarketData.Subscriber'
}

export enum ProfileSetting {
  ClientProfileKey = 'ClientProfileKey',
  IsProfessional = 'IsProfessional',
  SeenIe6Message = 'SeenIe6Message',
  SeenIe7Message = 'SeenIe7Message',
  CmcTieredClassicInterceptViewed = 'CmcTieredClassicInterceptViewed',
  CmcTieredActiveInvestorInterceptViewed = 'CmcTieredActiveInvestorInterceptViewed',
  CmcTieredPremiumTraderInterceptViewed = 'CmcTieredPremiumTraderInterceptViewed',
  JsDebugLevel = 'JsDebugLevel',
  PasswordLastUpdated = 'PasswordLastUpdated',
  PasswordForceChange = 'PasswordForceChange',
  BestExecutionPolicy = 'BestExecutionPolicy',
  MarketDataChosenPackage = 'MarketDataChosenPackage',
  MarketDataFreeSubscriptionExpiried = 'MarketDataFreeSubscriptionExpiried',
  MobileNumberUpdated = 'MobileNumberUpdated',
  MoversShakersTypePreference = 'MoversShakersTypePreference',
  MoversShakersViewPreference = 'MoversShakersViewPreference',
  ProfessionalInvestorResponse = 'ProfessionalInvestorResponse',
  RecentViewedStocks = 'RecentViewedStocks',
  SessionTimeout = 'SessionTimeout',
  SiteNotificationsEnabled = 'SiteNotificationsEnabled',
  TermsAndconditions = 'TermsAndconditions',
  TradingPinViewed = 'TradingPinViewed',
  OrderTicketPreference = 'OrderTicketPreference',
  CurrentTradingAccount = 'CurrentTradingAccount',
  TradingCentralNewsletter = 'TradingCentralNewsletter',
  TradingCentralWebData = 'TradingCentralWebData',
  IsIntermediaryCompanyMember = 'IsIntermediaryCompanyMember',
  TradingPinEnabled = 'TradingPinEnabled',
  UiAccountNavHidden = 'UiAccountNavHidden',
  UiSelectedCountryIsoCode = 'UiSelectedCountryIsoCode',
  MultiAccountSelectorMode = 'MultiAccountSelectorMode',
  MultiAccountSelectorSelectedAccounts = 'MultiAccountSelectorSelectedAccounts',
  SeenRivkinWelcomePopup = 'SeenRivkinWelcomePopup',
  IsPremium = 'IsPremium',
  EmailNotificationCount = 'Client.EmailNotificationCount',
  EmailAddressVerified = 'Client.EmailAddressVerified',
  ModernInterceptorDismissCount = 'Client.ModernInterceptorDismissCount',
  SeenBendigoLeveragedPromo = 'Client.HasSeenBendigoLeveragedPromo',
  USExtendedHoursEnabled = 'USExtendedHours.Enabled',
  ClientSeenFalconWelcomeTour = 'ClientSeenFalconWelcomeTour',
  BanyanTreeTncAccepted = 'BanyanTreeTncAccepted'
}

export type PendingOnlineApplicationsMode1 = {
  UnfinishedApplications?: unknown[];
  SubmittedApplications?: unknown[];
};

export interface OperationResultModel1 {
  Success: boolean;
  UiMessage: string;
  ExecutionTimeMs: number;
}

export interface Country {
  Label: string;
  IsoCode: string;
  Iso2Code: string;
  CurrencyCode: string;
  Selected: boolean;
}

export type StockQuery = {
  id: number;
  name: string;
};

export type NameValuePair = {
  name: string;
  value: string;
};

export enum SigninRequestType {
  Login = `Login`,
  AcquireSession = `AcquireSession`,
  TokenAuthorization = `TokenAuthorization`
}

export enum AccessRightsType {
  Bit = 1,
  DateTime = 2,
  Integer = 3,
  Real = 4,
  String = 5
}

export interface ClientAttributeSaveRequest1 extends CmcWebApiTypes.Types.WebApiHandlerRequest {
  AccessRight: ClientRoles;
  Value: string;
  AccessRightsType: AccessRightsType;
  ClientSetting: ClientSettings;
}

export interface ClientFeedbackAddRequest1 extends CmcWebApiTypes.Types.WebApiHandlerRequest {
  Source: string;
  Comments: string;
}

export interface ClientProfileSetting1 {
  Key: keyof typeof ProfileSetting;
  Value: string | boolean | number;
}

export interface ClientProfileSettingSave1 extends CmcWebApiTypes.Types.WebApiHandlerRequest {
  Settings: ClientProfileSetting1[];
}

export interface StockLendingSettings {
  ShowStockLendingInterceptor?: boolean;
}

export interface ClientProSubscriptionInfo {
  fetched: boolean;
  Success: boolean;
  EndDate: string;
  CancelToken: string;
}

export interface SalesforceTaskModel {
  Subject: string;
  Description: string;
  CaseType: string;
  CaseTeam: string;
}

export interface SalesforceCaseAddRequest1 extends CmcWebApiTypes.Types.WebApiHandlerRequest {
  SalesforceTask: SalesforceTaskModel;
  AccountNumber: string | undefined;
}
